import { useEffect, useState } from "react";
import { Bell, BellOff, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { useMember } from "@/contexts/MemberContext";
import { isPushSupported, getPermission, getSubscription, subscribeToPush, unsubscribeFromPush, ensureSubscriptionAlive } from "@/lib/push";

const DISMISS_KEY = "ncl_push_dismissed_at";
const DISMISS_TTL = 7 * 24 * 60 * 60 * 1000;

export function NotificationsOptIn() {
  const { t, lang } = useI18n();
  const { member } = useMember();
  const [show, setShow] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    (async () => {
      if (!isPushSupported()) { setSupported(false); return; }
      const perm = await getPermission();
      // AUTO-HEAL: if the OS silently revoked the subscription (iOS
      // Safari does this after a few "silent" pushes) but the user still
      // has permission=granted, re-subscribe transparently so delivery
      // resumes without any user action.
      if (perm === "granted") {
        try { await ensureSubscriptionAlive({ language: lang, memberId: member?.id }); } catch (_) { /* silent auto-heal */ }
      }
      const sub = await getSubscription();
      setSubscribed(!!sub && perm === "granted");

      try {
        const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
        if (at && Date.now() - at < DISMISS_TTL) return;
      } catch (_) { /* localStorage disabled */ }

      if (perm === "default" && !sub) setShow(true);
    })();
  }, [lang, member?.id]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* localStorage disabled */ }
    setShow(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush({ language: lang, memberId: member?.id });
      setSubscribed(true);
      setShow(false);
      toast.success(lang === "es" ? "Notificaciones activadas" : "Notifications enabled");
    } catch (err) {
      const msg = err?.message || "";
      if (msg === "permission_denied") {
        toast.error(lang === "es"
          ? "Permiso denegado. Actualiza permisos del navegador para activarlas."
          : "Permission denied. Update your browser permissions to enable.");
      } else {
        toast.error(t.common.error);
      }
    } finally { setBusy(false); }
  };

  if (!supported) return null;
  if (subscribed) return null;
  if (!show) return null;

  return (
    <div className="rounded-2xl border border-primary/20 bg-white p-4 shadow-sm ring-1 ring-black/5" data-testid="push-optin-banner">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Bell className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-serif-display text-base font-semibold leading-tight">
            {lang === "es" ? "Activa las notificaciones" : "Enable notifications"}
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {lang === "es"
              ? "Recibe avisos de eventos importantes y servicios proximos."
              : "Get notified about important upcoming events and services."}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={enable} disabled={busy} data-testid="push-optin-enable">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Bell className="mr-1 h-4 w-4" />}
              {lang === "es" ? "Activar" : "Enable"}
            </Button>
            <Button size="sm" variant="outline" onClick={dismiss} data-testid="push-optin-dismiss">
              {lang === "es" ? "Ahora no" : "Not now"}
            </Button>
          </div>
        </div>
        <button type="button" onClick={dismiss} aria-label="close" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Settings-style toggle button (used in Inicio sidebar or social page later if needed).
 */
export function NotificationsToggle({ className = "" }) {
  const { lang } = useI18n();
  const { member } = useMember();
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    (async () => {
      if (!isPushSupported()) { setSupported(false); return; }
      const perm = await getPermission();
      // Auto-heal revoked-by-OS subscriptions (see NotificationsOptIn above).
      if (perm === "granted") {
        try { await ensureSubscriptionAlive({ language: lang, memberId: member?.id }); } catch (_) { /* silent auto-heal */ }
      }
      const sub = await getSubscription();
      setSubscribed(!!sub && perm === "granted");
    })();
  }, [lang, member?.id]);

  if (!supported) return null;

  const onToggle = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        setSubscribed(false);
        toast.success(lang === "es" ? "Notificaciones desactivadas" : "Notifications disabled");
      } else {
        await subscribeToPush({ language: lang, memberId: member?.id });
        setSubscribed(true);
        toast.success(lang === "es" ? "Notificaciones activadas" : "Notifications enabled");
      }
    } catch (err) {
      toast.error(err?.message === "permission_denied"
        ? (lang === "es" ? "Permiso denegado." : "Permission denied.")
        : (lang === "es" ? "Error" : "Error"));
    } finally { setBusy(false); }
  };

  return (
    <Button variant="outline" size="sm" onClick={onToggle} disabled={busy} className={className} data-testid="push-toggle">
      {subscribed ? <Bell className="mr-1 h-4 w-4 text-primary" /> : <BellOff className="mr-1 h-4 w-4" />}
      {subscribed
        ? (lang === "es" ? "Notificaciones activas" : "Notifications on")
        : (lang === "es" ? "Activar notificaciones" : "Enable notifications")}
    </Button>
  );
}
